import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { WorkMirrorStartModal } from '../components/WorkMirrorStartModal';
import { MobileNav } from '../components/MobileNav';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-4733" />;
export function WorkMirror() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return <>
      <Helmet data-id="element-4734">
        <title data-id="element-4735">Work Mirror — See Your Operating System | Lazy Monk Labs</title>
        <meta name="description" content="Work Mirror reveals the pattern behind your work decisions — whether things are going well or not." data-id="element-4736" />
        <meta name="robots" content="index, follow" data-id="element-4737" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4738">
        <MobileNav data-id="element-4739" />

        <main className="pt-20 sm:pt-24 pb-12 sm:pb-16" data-id="element-4740">
          {/* 1. HERO */}
          <section className="relative py-20 sm:py-24 md:py-32 lg:py-40 px-4 sm:px-6 overflow-hidden" data-id="element-4741">
            <CoordinateGrid data-id="element-4742" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-8 sm:space-y-12" data-id="element-4743">
              <div className="space-y-6 sm:space-y-8" data-id="element-4744">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif leading-[1.15] text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4745">
                  You're already good at what you do.
                  <br data-id="element-4746" />
                  This shows you how you do it.
                </h1>

                <p className="text-lg sm:text-xl md:text-2xl text-gray-400 font-light leading-relaxed max-w-3xl" data-id="element-4747">
                  Work Mirror reveals the pattern behind your work decisions —
                  whether things are going well or not.
                </p>

                <p className="text-base sm:text-lg text-gray-500 leading-relaxed max-w-2xl pt-4" data-id="element-4748">
                  Some people come here because they're stuck.
                  <br data-id="element-4749" />
                  Others come because they want to see the system they're
                  already running.
                </p>
              </div>

              <div className="pt-6 sm:pt-8 space-y-3 sm:space-y-4" data-id="element-4750">
                <button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto inline-block px-8 sm:px-12 py-4 sm:py-5 bg-[#F5F5F5] text-[#0A0A0A] font-medium text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-4751">
                  Start Work Mirror
                </button>
                <p className="text-xs sm:text-sm text-gray-600" data-id="element-4752">
                  Short calibration. Free to try.
                </p>
              </div>
            </div>
          </section>

          {/* 2. CORE CLAIM */}
          <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-4753">
            <div className="max-w-4xl mx-auto" data-id="element-4754">
              <div className="space-y-8 sm:space-y-12" data-id="element-4755">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4756">
                  Performance hides patterns.
                </h2>

                <div className="space-y-6 sm:space-y-8 text-base sm:text-lg text-gray-400 leading-relaxed max-w-2xl" data-id="element-4757">
                  <p data-id="element-4758">
                    When things are going well,
                    <br data-id="element-4759" />
                    patterns stay invisible.
                  </p>

                  <p data-id="element-4760">
                    When pressure increases,
                    <br data-id="element-4761" />
                    they surface.
                  </p>

                  <p className="text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-4762">
                    Work Mirror makes structure visible early — before it costs
                    you time, identity, or momentum.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 3. WHAT IT ACTUALLY DOES */}
          <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-4763">
            <div className="max-w-4xl mx-auto" data-id="element-4764">
              <div className="space-y-8 sm:space-y-12" data-id="element-4765">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4766">
                  See your operating system in real time.
                </h2>

                <div className="space-y-6 sm:space-y-8" data-id="element-4767">
                  <div className="text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4768">
                    <p data-id="element-4769">Not outcomes.</p>
                    <p data-id="element-4770">Not goals.</p>
                    <p className="pt-4 text-[#F5F5F5]" data-id="element-4771">
                      The way you decide,
                      <br data-id="element-4772" />
                      commit,
                      <br data-id="element-4773" />
                      delay,
                      <br data-id="element-4774" />
                      and reset.
                    </p>
                  </div>

                  <div className="pt-8 border-t border-[#2B2B2B] space-y-6" data-id="element-4775">
                    <p className="text-sm font-mono text-gray-600 uppercase tracking-wider" data-id="element-4776">
                      How it works
                    </p>
                    <div className="space-y-4 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4777">
                      <p data-id="element-4778">• A short calibration locates your system</p>
                      <p data-id="element-4779">• You log real work moments</p>
                      <p data-id="element-4780">
                        • The mirror reflects recurring tensions, loops, and
                        phase shifts
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 italic pt-6" data-id="element-4781">
                    No advice. No optimization. Just visibility.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 4. STRUGGLE (REPOSITIONED) */}
          <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-4782">
            <div className="max-w-4xl mx-auto" data-id="element-4783">
              <div className="space-y-8 sm:space-y-12" data-id="element-4784">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4785">
                  Struggle is one signal. Not the whole story.
                </h2>

                <div className="space-y-6 sm:space-y-8 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4786">
                  <p data-id="element-4787">Patterns appear when:</p>
                  <div className="pl-4 sm:pl-6 border-l-2 border-[#2B2B2B] space-y-3" data-id="element-4788">
                    <p data-id="element-4789">• you're stuck</p>
                    <p data-id="element-4790">• you're scaling</p>
                    <p data-id="element-4791">• stakes increase</p>
                    <p data-id="element-4792">• identity is on the line</p>
                  </div>

                  <p className="text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-4793">
                    Work Mirror is useful before, during, and after those
                    moments.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. WHO THIS IS FOR */}
          <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-4794">
            <div className="max-w-4xl mx-auto" data-id="element-4795">
              <div className="space-y-8 sm:space-y-12" data-id="element-4796">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4797">
                  For people who care about how they operate.
                </h2>

                <div className="space-y-6 sm:space-y-8 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-4798">
                  <div className="space-y-4" data-id="element-4799">
                    <p data-id="element-4800">• You're doing well but want cleaner leverage</p>
                    <p data-id="element-4801">• You sense patterns before you can explain them</p>
                    <p data-id="element-4802">• You don't want advice — you want to see the loop</p>
                    <p data-id="element-4803">• Your decisions compound over time</p>
                  </div>

                  <p className="text-[#F5F5F5] pt-6 border-t border-[#2B2B2B]" data-id="element-4804">
                    If you want fixes or motivation, this isn't it.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 6. BOUNDARIES */}
          <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-4805">
            <div className="max-w-4xl mx-auto" data-id="element-4806">
              <div className="space-y-8 sm:space-y-12" data-id="element-4807">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4808">
                  This is not self-help.
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 sm:gap-16" data-id="element-4809">
                  <div className="space-y-4 sm:space-y-6" data-id="element-4810">
                    <h3 className="text-lg sm:text-xl font-medium text-gray-500" data-id="element-4811">
                      This is NOT
                    </h3>
                    <div className="space-y-3 text-base sm:text-lg text-gray-500" data-id="element-4812">
                      <p data-id="element-4813">• Coaching</p>
                      <p data-id="element-4814">• Therapy</p>
                      <p data-id="element-4815">• Productivity advice</p>
                    </div>
                  </div>

                  <div className="space-y-4 sm:space-y-6" data-id="element-4816">
                    <h3 className="text-lg sm:text-xl font-medium text-[#F5F5F5]" data-id="element-4817">
                      This IS
                    </h3>
                    <p className="text-xl sm:text-2xl text-[#F5F5F5] leading-relaxed" data-id="element-4818">
                      A mirror for seeing the system behind your work.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 7. FINAL CTA */}
          <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 relative overflow-hidden border-t border-[#2B2B2B]" data-id="element-4819">
            <CoordinateGrid data-id="element-4820" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-8 sm:space-y-12" data-id="element-4821">
              <div className="space-y-6 sm:space-y-8" data-id="element-4822">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-[#F5F5F5] leading-tight" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-4823">
                  The system is already running.
                </h2>

                <p className="text-lg sm:text-xl text-gray-400 leading-relaxed" data-id="element-4824">
                  Work Mirror lets you see it.
                </p>
              </div>

              <div className="pt-6 sm:pt-8 space-y-3 sm:space-y-4" data-id="element-4825">
                <button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto inline-block px-8 sm:px-12 py-4 sm:py-5 bg-[#F5F5F5] text-[#0A0A0A] font-medium text-sm tracking-widest uppercase hover:bg-gray-200 transition-colors" data-id="element-4826">
                  Start Work Mirror
                </button>
                <p className="text-xs sm:text-sm text-gray-600" data-id="element-4827">
                  Calibration takes ~3 minutes.
                </p>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-[#2B2B2B]" data-id="element-4828">
          <div className="max-w-6xl mx-auto" data-id="element-4829">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 sm:gap-8" data-id="element-4830">
              <div data-id="element-4831">
                <p className="text-sm text-gray-500 font-mono" data-id="element-4832">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-4833">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8 text-sm text-gray-500" data-id="element-4834">
                <a href="https://decoder-website.vercel.app/reports" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4835">
                  Decodes
                </a>
                <Link to="/mirrors/work" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4836">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4837">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4838">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-4839">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-4840">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>

        {/* Work Mirror Start Modal */}
        <WorkMirrorStartModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-4841" />
      </div>
    </>;
}